"use client";

import { env } from "@/env";
import {
  captureAttributionFromUrl,
  getAttributionContext,
  recordEventTouch,
} from "./attribution";
import {
  captureEvent,
  getDistinctId,
  registerProperties,
} from "./posthog-client";

export type SalesEventKind =
  | "page_view"
  | "session_start"
  | "session_end"
  | "form_start"
  | "form_submit"
  | "signin_started"
  | "signin_completed"
  | "signup_started"
  | "signup_completed"
  | "trial_started"
  | "payment_succeeded"
  | "experiment_exposure";

interface SalesEventPayload {
  kind: SalesEventKind;
  ftAid: string;
  ts: number;
  gclid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPage?: string;
  referrer?: string;
  sessionId?: string;
  userId?: string;
  orgId?: string;
  device?: string;
  geo?: string;
  engaged?: boolean;
  value?: number;
  currency?: string;
  metadata?: Record<string, unknown>;
}

export interface SalesEventInput {
  kind: SalesEventKind;
  value?: number;
  currency?: string;
  metadata?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  orgId?: string;
  device?: string;
  geo?: string;
  engaged?: boolean;
}

const ingestUrl = env.NEXT_PUBLIC_ANALYTICS_INGEST_URL;

export const sendSalesEvent = async (input: SalesEventInput) => {
  if (typeof window === "undefined") return;

  const tEventId = crypto.randomUUID();

  // Ensure attribution is captured ASAP
  captureAttributionFromUrl();
  const attribution = getAttributionContext();

  const payload: SalesEventPayload = {
    kind: input.kind,
    ts: Date.now(),
    ftAid: attribution.ftAid,
    value: input.value,
    currency: input.currency,
    metadata: {
      ...(input.metadata || {}),
      t_event_id: tEventId,
      source: "web",
    },
    userId: input.userId,
    sessionId: input.sessionId,
    orgId: input.orgId,
    device: input.device,
    geo: input.geo,
    engaged: input.engaged,
  };

  const source = attribution.lastTouch ?? attribution.firstTouch;
  if (source) {
    Object.assign(payload, {
      gclid: source.gclid,
      utmSource: source.utm_source,
      utmMedium: source.utm_medium,
      utmCampaign: source.utm_campaign,
      utmTerm: source.utm_term,
      utmContent: source.utm_content,
      landingPage: source.landing_page,
      referrer: source.referrer,
    });
    recordEventTouch(source);
  }

  const shouldForwardToPostHog = input.kind !== "page_view";

  if (shouldForwardToPostHog) {
    try {
      if (attribution.ftAid) registerProperties({ ft_aid: attribution.ftAid });
      const phDistinctId = getDistinctId();
      const posthogPayload: Record<string, unknown> = {
        ...payload,
        t_event_id: tEventId,
        ...(phDistinctId ? { phDistinctId } : {}),
      };
      captureEvent(input.kind, posthogPayload);
    } catch (error) {
      console.error("[sales-events] posthog dual-write failed", {
        kind: input.kind,
        error,
      });
    }
  }

  // Send to Worker ingest
  if (!ingestUrl) return;
  try {
    await fetch(ingestUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("[sales-events] worker ingest failed", {
      kind: input.kind,
      ingestUrl,
      error,
    });
  }
};
